import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ChatIcon from "@mui/icons-material/Chat";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentIcon from "@mui/icons-material/Payment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PeopleIcon from "@mui/icons-material/People";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, useScroll, useSpring } from "framer-motion";
import { useTranslation } from "react-i18next";

const ProgressBar = styled(motion.div)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "5px",
  background: theme.palette.primary.main,
  transformOrigin: "0%",
  zIndex: 1000,
}));

const FeatureCard = styled(motion.div)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(4),
  borderRadius: 24,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  overflow: "hidden",
  height: "100%",
  border: "1px solid rgba(0,0,0,0.06)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
    transform: "translateY(-4px)",
  },
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: 64,
  height: 64,
  borderRadius: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2.5),
  background: `linear-gradient(135deg, ${theme.palette[color].light}20, ${theme.palette[color].main}30)`,
  color: theme.palette[color].main,
}));

function AboutSection() {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const containerRef = useRef(null);
  const [activeCard, setActiveCard] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const items = [
    {
      icon: <ChatIcon sx={{ fontSize: 32 }} />,
      title: t("about.features.0.title") || "Communication",
      desc:
        t("about.features.0.desc") ||
        "Seamless team communication and collaboration tools",
      color: "primary",
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 32 }} />,
      title: t("about.features.1.title") || "Attendance",
      desc:
        t("about.features.1.desc") ||
        "Smart attendance tracking and management system",
      color: "secondary",
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 32 }} />,
      title: t("about.features.2.title") || "Payroll",
      desc:
        t("about.features.2.desc") ||
        "Automated payroll processing and management",
      color: "info",
    },
    {
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 32 }} />,
      title: t("about.features.3.title") || "Task Management",
      desc:
        t("about.features.3.desc") ||
        "Efficient task tracking and project management",
      color: "success",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      title: t("about.features.4.title") || "HR Management",
      desc:
        t("about.features.4.desc") ||
        "Complete HR solutions for your organization",
      color: "warning",
    },
    {
      icon: <GroupIcon sx={{ fontSize: 32 }} />,
      title: t("about.features.5.title") || "Team Collaboration",
      desc:
        t("about.features.5.desc") ||
        "Enhanced team productivity and collaboration",
      color: "error",
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 32 }} />,
      title: t("about.features.6.title") || "Business Analytics",
      desc:
        t("about.features.6.desc") ||
        "Insightful analytics for better decisions",
      color: "primary",
    },
  ];

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const scrollPercentage = Math.max(
        0,
        Math.min(
          1,
          (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
        )
      );

      const cardIndex = Math.min(
        items.length - 1,
        Math.floor(scrollPercentage * items.length * 1.5)
      );

      setActiveCard(cardIndex);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, items.length]);

  return (
    <>
      <ProgressBar style={{ scaleX }} />
      <Box
        id="about"
        ref={containerRef}
        sx={{
          py: { xs: 8, md: 16 },
          minHeight: { xs: "auto", md: "200vh" },
          position: "relative",
          background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, white 50%, ${theme.palette.background.default} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 6, md: 8 },
              alignItems: { xs: "flex-start", md: "flex-start" },
            }}
          >
            {/* Left Side - Sticky Text Content */}
            <Box
              sx={{
                flex: { xs: "1", md: "0 0 45%" },
                position: { xs: "relative", md: "sticky" },
                top: { xs: 0, md: 180 },
                height: { xs: "auto", md: "fit-content" },
                zIndex: 1,
              }}
            >
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{
                    mb: 3,
                    fontSize: { xs: "2rem", md: "3rem" },
                    lineHeight: 1.2,
                    color: "#323B4B",
                    background: `linear-gradient(135deg, #323B4B 0%, ${theme.palette.primary.main} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {t("about.title") || "About djoglo"}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontSize: { xs: "1rem", md: "1.125rem" },
                    mb: 4,
                    fontWeight: 400,
                  }}
                >
                  {t("about.description") ||
                    "djoglo is an innovative tech product that provides digital solutions to support your business productivity. We focus on developing applications that facilitate communication, attendance management, and employee payroll processing efficiently and seamlessly integrated."}
                </Typography>

                {/* Feature Counter - Desktop Only */}
                {!isMobile && (
                  <Box sx={{ mt: 6 }}>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{ color: theme.palette.primary.main, mb: 1 }}
                    >
                      {items.length}+
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Powerful Features
                    </Typography>
                  </Box>
                )}
              </motion.div>
            </Box>

            {/* Right Side - Scrolling Cards */}
            <Box
              sx={{
                flex: { xs: "1", md: "0 0 50%" },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {items.map((item, index) => {
                const opacity = isMobile ? 1 : activeCard >= index ? 1 : 0.3;

                const scale = isMobile ? 1 : activeCard === index ? 1 : 0.95;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.5,
                      delay: isMobile ? index * 0.1 : 0,
                      ease: "easeOut",
                    }}
                  >
                    <FeatureCard
                      style={{
                        opacity: opacity,
                        scale: scale,
                      }}
                      transition={{
                        duration: 0.3,
                        ease: "easeOut",
                      }}
                    >
                      <IconWrapper color={item.color}>{item.icon}</IconWrapper>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{
                          mb: 1.5,
                          color: "#323B4B",
                          fontSize: { xs: "1.25rem", md: "1.5rem" },
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.7,
                          fontSize: { xs: "0.9rem", md: "1rem" },
                        }}
                      >
                        {item.desc}
                      </Typography>

                      {/* Decorative gradient */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: `linear-gradient(90deg, ${
                            theme.palette[item.color].main
                          }, ${theme.palette[item.color].light})`,
                          opacity: 0.6,
                        }}
                      />
                    </FeatureCard>
                  </motion.div>
                );
              })}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default AboutSection;
